import CT from '@marketplace-ai/tags';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  AsyncSelect,
  Button,
  FormActions,
  FormCell,
  FormField,
  FormLayout,
  FormRow,
  FormSection,
  Multiselect,
  TextInput,
} from '~/components';
import { SlideInPanel } from '~/components/SlideInPanel';
import { STATES } from '~/constants';
import { SubspecialtyFilter } from '~/features/market/listings/components/SubspecialtyFields';
import { useResetSubspecialties } from '~/features/market/listings/hooks/useSubspecialties';
import { loadAOIOptionByProviderCategory } from '~/features/market/listings/msaas-listing/wizard-steps/basic-info/utils';
import { useMsaasListingSearchRef } from '~/features/market/marketSelectors';
import { MarketContentPanel } from '~/features/shared/market-content';
import { loadCategoryOptions } from '~/features/vault/profile/api';
import { useListingProviderCategories } from '../../ProviderCategoriesContext';
import { SearchAndFilterValues } from './types';

const TAGS = CT.listingSearch.searchForm;

export const SearchAndFilter = () => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  const lookup = useListingProviderCategories();
  const loadCategories = lookup?.loadCategories;
  const categoryOptions = lookup?.categories?.map((category) => ({
    value: category.id,
    label: category.providerCategoryCode,
  }));
  // Keep this loader stable while the shared request settles, so a pending
  // typed query receives its result without resetting input, focus or cache.
  const loadSharedCategoryOptions = useCallback(
    async (input: string) => {
      if (!loadCategories) return loadCategoryOptions(input);
      const categories = await loadCategories();
      return categories
        .map((category) => ({ value: category.id, label: category.providerCategoryCode }))
        .filter((option) => option.label.toLowerCase().includes(input.toLowerCase()));
    },
    [loadCategories]
  );
  const form = useFormContext();
  const { reset, watch, handleSubmit } = form;
  useResetSubspecialties(form, { clearSpecialtyWithCategory: { empty: null } });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  if (!msaasListingsSearchRef) return null;
  const handleFilterClick = () => {
    setIsFilterPanelOpen(true);
  };

  const handleClearAll = () => {
    reset();
    handleSearch();
  };

  // [MCV-1548] Validation and searching used to be disconnected: the schema only
  // ever ran on blur, where it had nothing useful to say, and the search read
  // getValues() directly so a bad filter was never caught. Going through
  // handleSubmit also means the API receives the resolver's parsed output, so
  // the numeric filters arrive as numbers rather than raw input strings.
  // The invalid arm has to open the filter panel: every field the schema can
  // reject lives inside it, and SlideInPanel unmounts its children when closed,
  // so a top-bar Search on a bad range would otherwise be a dead button.
  const submitSearch = (onSearched?: () => void) =>
    handleSubmit(
      (values) => {
        msaasListingsSearchRef.send({
          type: 'ON_SEARCH',
          payload: { filters: values as SearchAndFilterValues },
        });
        onSearched?.();
      },
      () => setIsFilterPanelOpen(true)
    )();

  const handleSearch = () => submitSearch();

  const handleSlideInSearch = () => submitSearch(() => setIsFilterPanelOpen(false));

  const providerCategory = watch('providerCategory');
  const hasSpecialty = !!watch('aoiSpecialty')?.value;

  const FILTERS = [
    {
      key: 'providerCategory',
      node: (
        <AsyncSelect
          name={'providerCategory'}
          cyTag={TAGS.providerCategory}
          label={'Provider Category'}
          minChars={false}
          defaultOptions={categoryOptions ?? true}
          loadOptions={loadSharedCategoryOptions}
        />
      ),
    },
    {
      key: 'aoiSpecialty',
      node: (
        <AsyncSelect
          name={'aoiSpecialty'}
          cyTag={TAGS.aoiSpecialty}
          label={'Provider Specialty'}
          minChars={false}
          key={'aoiSpecialty-' + providerCategory?.value}
          loadOptions={(inputValue: string) => {
            if (providerCategory?.value) {
              return loadAOIOptionByProviderCategory(providerCategory.value, inputValue);
            }
            return Promise.resolve([]);
          }}
        />
      ),
    },
    {
      key: 'states',
      node: (
        <Multiselect
          name={'states'}
          cyTag={TAGS.states}
          label={'States'}
          options={STATES}
          useShortValue
        />
      ),
    },
    {
      key: 'workLocation',
      node: (
        <Multiselect
          isClearable={true}
          name={'workLocation'}
          cyTag={TAGS.workLocation}
          label={'Work Location'}
          options={[
            { value: 'ONSITE', label: 'Onsite' },
            { value: 'PARTIAL_REMOTE', label: 'Hybrid' },
            { value: 'REMOTE', label: 'Remote' },
          ]}
        />
      ),
    },
    {
      key: 'fteRange',
      // A range is one visibly labelled field made of two inputs, so the visible
      // label names the pair rather than either input. `labelFor={false}` is
      // FormField's composite-widget mode: it renders the shared FormLabel
      // without an `htmlFor` and publishes it as `#fteRange-label`, which the
      // group below claims through `aria-labelledby` -- the wiring TransferList
      // already uses, and the half `labelFor={false}` exists to be paired with.
      //
      // A group name is not an input name, so each input also carries its own
      // `sr-only` <label>. Without them both number fields reach the
      // accessibility tree unnamed, and nothing tells the low end of the range
      // from the high end. `sr-only` is position:absolute, so the labels are out
      // of flow and the row renders exactly as before.
      node: (
        <FormField id="fteRange" label="FTE Range" labelFor={false}>
          <div
            role="group"
            aria-labelledby="fteRange-label"
            className="flex flex-row gap-2 items-center"
          >
            <label htmlFor="minFte" className="sr-only">
              Minimum FTE
            </label>
            <TextInput name={'minFte'} cyTag={TAGS.minFte} type="number" />
            <span>-</span>
            <label htmlFor="maxFte" className="sr-only">
              Maximum FTE
            </label>
            <TextInput name={'maxFte'} cyTag={TAGS.maxFte} type="number" />
          </div>
        </FormField>
      ),
    },
    {
      key: 'contractValue',
      node: (
        <FormField id="contractValue" label="Contract Value" labelFor={false}>
          <div
            role="group"
            aria-labelledby="contractValue-label"
            className="flex flex-row gap-2 items-center"
          >
            <label htmlFor="minContractValue" className="sr-only">
              Minimum Contract Value
            </label>
            <TextInput name={'minContractValue'} cyTag={TAGS.minContractValue} type="number" />
            <span>-</span>
            <label htmlFor="maxContractValue" className="sr-only">
              Maximum Contract Value
            </label>
            <TextInput name={'maxContractValue'} cyTag={TAGS.maxContractValue} type="number" />
          </div>
        </FormField>
      ),
    },
    {
      key: 'startDate',
      node: (
        <TextInput
          name={'startDate'}
          cyTag={TAGS.startDate}
          label={'Start Date After'}
          type="date"
        />
      ),
    },
  ];
  // The top bar keeps its four fields; subspecialties sit under Specialty in All Filters.
  const panelFilters = hasSpecialty
    ? [
        ...FILTERS.slice(0, 2),
        {
          key: 'subspecialties',
          node: <SubspecialtyFilter cyTag={TAGS.subspecialties} />,
        },
        ...FILTERS.slice(2),
      ]
    : FILTERS;
  return (
    <MarketContentPanel variant="compact">
      <SlideInPanel
        open={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        title="All Filters"
      >
        <FormLayout>
          <FormSection componentsPerLine={1}>
            {panelFilters.map((filter) => (
              <FormRow key={filter.key}>{filter.node}</FormRow>
            ))}
          </FormSection>
          <FormActions>
            <Button label="Apply Filters" onClick={handleSlideInSearch} />
          </FormActions>
        </FormLayout>
      </SlideInPanel>
      <div className="flex items-center justify-between mb-1">
        <h3 className="sub-heading-sb">Filters</h3>
        <button
          type="button"
          onClick={handleClearAll}
          className="text-sm font-medium text-primary-50 hover:text-primary-t-300 transition-colors"
        >
          Clear All
        </button>
      </div>
      <FormSection componentsPerLine={4}>
        {FILTERS.slice(0, 4).map((filter) => (
          <FormRow key={'top-filter-bar-' + filter.key}>{filter.node}</FormRow>
        ))}
        {/* The actions are not a field, so they take a full-width cell of their
            own rather than a fifth filter column; FormSection's gap-y supplies
            the space that the removed per-button `mb-4` shims used to fake. */}
        <FormCell span={12}>
          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
            <div className="shrink-0">
              <Button
                label={'All Filters'}
                colorVariant="tertiary"
                onClick={handleFilterClick}
                variant="rounded"
              />
            </div>
            <div className="shrink-0">
              <Button label={'Search'} onClick={handleSearch} variant="rounded" />
            </div>
          </div>
        </FormCell>
      </FormSection>
    </MarketContentPanel>
  );
};
